import * as React from 'react'

import { ApolloProvider } from '@apollo/client'
import { ContainsEditableProps, useEditor } from '@toasttab/sites-components'
import { v4 as uuidv4 } from 'uuid'

import { DoMenus_GetMenuGroupsGroup as DoMenusGetMenuGroupsGroup } from 'src/apollo/onlineOrdering'
import { BorderAndShadowConfig, MenuItemIdentifier } from 'src/apollo/sites'

import { useBulkCalls } from 'shared/components/common/dynamic_menus/BulkCallsContext'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

import GroupedMenuItems from './GroupedMenuItems'
import MenuGroupSkeleton from './MenuGroupSkeleton'
import { MenuGroupBlockConfig } from './types'

const placeholderMenuGroup = {
  name: 'Menu Group',
  items: [
    { guid: uuidv4() },
    { guid: uuidv4() },
    { guid: uuidv4() },
    { guid: uuidv4() }
  ]
} as DoMenusGetMenuGroupsGroup

export type MenuGroupEditableProps = {
  classNames?: string
  config: MenuGroupBlockConfig | null
  styles?: any
  testId?: string
  items: MenuItemIdentifier[]
  description: string
  name: string
  borderAndShadowConfig?: BorderAndShadowConfig | null
  overrideGlobalBorderAndShadowConfig?: boolean | null
} & ContainsEditableProps

export const EditableFeaturedMenuItems = (props: MenuGroupEditableProps) => {
  const { menuItems, loading } = useBulkCalls()
  const { useEditableRef } = useEditor()
  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'featured menu items',
    displayName: 'Featured Menu Items',
    actions: ['delete'],
    path: props.editPath,
    schema: { fields: [] }
  })

  const itemsToRender = React.useMemo(() => {
    if (!props.items?.length || !menuItems.length)
      return placeholderMenuGroup.items
    return props.items.map((item: any) => {
      return menuItems.find((menuItem: any) => menuItem.guid === item.itemGuid)
    })
  }, [menuItems, props.items])

  if (loading) {
    return (
      <MenuGroupSkeleton
        columns={props.config?.appearance?.columns || 1}
        itemCount={props.items?.length || 4}
        showTitle={props.config?.appearance?.showMenuGroupTitle !== false}
      />
    )
  }

  return (
    <GroupedMenuItems
      {...props}
      itemsToRender={itemsToRender}
      description={props.description}
      name={props.name}
      editableRef={editableRef}
    />
  )
}

const ApolloWrappedEditableFeaturedMenuItems = (
  props: MenuGroupEditableProps
) => {
  const ooClient = useOOClient()
  return (
    <ApolloProvider client={ooClient}>
      <EditableFeaturedMenuItems {...props} />
    </ApolloProvider>
  )
}

export default ApolloWrappedEditableFeaturedMenuItems
